// Active workspace (X-Workspace-Id). Single source of truth in
// lib/active-workspace (read by the axios interceptor). On switch: persist,
// refetch /auth/me (scoped permissions) and invalidate all queries.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/auth/useAuth";
import { getActiveWorkspaceId, setActiveWorkspaceId as persistWorkspaceId } from "@/lib/active-workspace";

interface WorkspaceCtxValue {
  activeWorkspaceId: number | null;
  setActiveWorkspaceId: (id: number) => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceCtxValue>({
  activeWorkspaceId: null,
  setActiveWorkspaceId: async () => {},
});

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<number | null>(() => getActiveWorkspaceId());

  // Bootstrap from the backend default when nothing is stored locally.
  const defaultWorkspaceId = user?.activeWorkspaceId ?? null;
  useEffect(() => {
    if (activeId == null && defaultWorkspaceId != null) {
      persistWorkspaceId(defaultWorkspaceId);
      setActiveId(defaultWorkspaceId);
    }
  }, [activeId, defaultWorkspaceId]);

  const setActiveWorkspaceId = useCallback(
    async (id: number) => {
      if (id === activeId) return;
      persistWorkspaceId(id);
      setActiveId(id);
      await refresh();
      await qc.invalidateQueries();
    },
    [activeId, refresh, qc],
  );

  const value = useMemo<WorkspaceCtxValue>(
    () => ({ activeWorkspaceId: activeId, setActiveWorkspaceId }),
    [activeId, setActiveWorkspaceId],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActiveWorkspace(): WorkspaceCtxValue {
  return useContext(WorkspaceContext);
}
